import { useState, useEffect, useRef } from "react";
import { Shield, Key, Mail, ArrowLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function TwoFactorAuth({ 
  onVerificationComplete, 
  onBack, 
  email, 
  isSetup = false 
}) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const initialCodeSentRef = useRef(false);

  // In modalità login (non setup), genera automaticamente un codice 2FA
  // quando la schermata viene mostrata, così l'utente riceve subito l'email.
  // useRef evita doppia esecuzione (es. Strict Mode) e doppio toast.
  useEffect(() => {
    if (!isSetup && !setupData && email && !initialCodeSentRef.current) {
      initialCodeSentRef.current = true;
      setLoading(true);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/generate-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })
        .then(async (response) => {
          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || 'Errore generazione codice 2FA');
          }
          return response.json();
        })
        .then(() => {
          toast.info('Codice 2FA inviato via email');
        })
        .catch((error) => {
          console.error('Error generating login 2FA code:', error);
          initialCodeSentRef.current = false; // permette retry
          toast.error(error.message || 'Errore generazione codice 2FA');
        })
        .finally(() => setLoading(false));
    }
  }, [isSetup, setupData, email]);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('affidya_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore setup 2FA');
      }

      const result = await response.json();
      setSetupData(result);
      
      // Mostra il codice nel console per testing
      //console.log('🔐 CODICE 2FA PER TESTING:', result.test_code || 'Controlla i log del backend');
      
      toast.success('Codice 2FA inviato via email');

    } catch (error) {
      console.error('Setup 2FA error:', error);
      toast.error(error.message || 'Errore configurazione 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSetup && setupData) {
        // Setup phase
        const token = localStorage.getItem('affidya_token');
        
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            setup_id: setupData.setup_id,
            code: code
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Codice 2FA non valido');
        }

        toast.success('2FA configurato con successo!');
        onVerificationComplete();
        
      } else {
        // Login verification phase - prima genera codice se necessario
        if (!code) {
          const generateResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/generate-code`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: email
            })
          });

          if (generateResponse.ok) {
            const generateData = await generateResponse.json();
            toast.info('Codice 2FA inviato via email');
            return;
          }
        }

        // Verifica codice
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/login-verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            code: code
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Codice 2FA non valido');
        }

        const data = await response.json();
        
        // Se richiede codice, mostra il messaggio appropriato
        if (data.requires_code) {
          toast.info('Codice 2FA inviato via email');
          return;
        }
        
        // Salva i token in modo coerente con AuthContext
        localStorage.setItem('affidya_token', data.token);
        if (data.refresh_token) {
          localStorage.setItem('affidya_refresh_token', data.refresh_token);
        }
        
        onVerificationComplete(data);
      }

    } catch (error) {
      console.error('2FA verify error:', error);
      toast.error(error.message || 'Codice 2FA non valido');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('affidya_token');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/resend-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Errore invio codice');
      }

      toast.success('Nuovo codice 2FA inviato via email');
      
    } catch (error) {
      console.error('Resend code error:', error);
      toast.error(error.message || 'Errore invio codice');
    } finally {
      setLoading(false);
    }
  };

  // Se è setup e non abbiamo ancora i dati di setup
  if (isSetup && !setupData) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-lg">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <CardTitle className="text-2xl text-slate-800">
                Proteggi il tuo account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <Mail className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  Autenticazione a Due Fattori
                </h3>
                <p className="text-slate-600 mb-6">
                  Aggiungi un livello extra di sicurezza al tuo account. 
                  Riceverai un codice via email per verificare la tua identità.
                </p>
              </div>

              <Button
                onClick={handleSetup}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Configurazione in corso...
                  </div>
                ) : (
                  'Configura 2FA'
                )}
              </Button>

              <div className="text-xs text-slate-500 text-center">
                <p>L'autenticazione a due fattori è obbligatoria per accedere ad Affidya.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-4">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <CardTitle className="text-2xl text-slate-800">
              {isSetup ? 'Verifica Configurazione' : 'Verifica in Due Fattori'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <Key className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">
                {isSetup 
                  ? 'Inserisci il codice ricevuto via email per completare la configurazione'
                  : `Inserisci il codice inviato a ${email}`
                }
              </p>
            </div>

            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <Input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-lg font-mono"
                  maxLength={6}
                  required
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Codice a 6 cifre
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading || code.length !== 6}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Verifica in corso...
                  </div>
                ) : (
                  isSetup ? 'Completa Configurazione' : 'Verifica Codice'
                )}
              </Button>

              {!isSetup && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    // Genera codice senza submit form
                    setLoading(true);
                    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/2fa/generate-code`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        email: email
                      })
                    })
                    .then(response => response.json())
                    .then(data => {
                      toast.info('Codice 2FA inviato via email');
                    })
                    .catch(error => {
                      console.error('Error generating code:', error);
                      toast.error('Errore generazione codice');
                    })
                    .finally(() => setLoading(false));
                  }}
                  className="w-full border-blue-500 text-blue-600 hover:bg-blue-50"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Generazione in corso...
                    </div>
                  ) : (
                    'Genera Nuovo Codice'
                  )}
                </Button>
              )}
            </form>

            <div className="text-center">
              <button
                onClick={resendCode}
                className="text-sm text-blue-600 hover:text-blue-700"
                disabled={loading}
              >
                Non hai ricevuto il codice? Invia di nuovo
              </button>
            </div>

            {!isSetup && (
              <Button
                variant="ghost"
                onClick={onBack}
                className="w-full text-slate-600 hover:text-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Torna al login
              </Button>
            )}

            <div className="text-xs text-slate-500 text-center bg-blue-50 p-3 rounded-lg">
              <p className="font-medium text-blue-800">🔐 Sicurezza Avanzata</p>
              <p className="text-blue-700 mt-1">
                L'autenticazione a due fattori protegge il tuo account da accessi non autorizzati
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
